import { PageConfig } from '@jupyterlab/coreutils'
import { isClientDevEnv } from '@/client-env'

/**
 * Route the Pyodide kernel through our own CloudFront proxy of jsDelivr
 * (/pyodide-cdn/*) instead of hitting cdn.jsdelivr.net directly, so users whose
 * network/edge can't get a clean copy from jsDelivr download the Python runtime
 * + packages from us. CloudFront proxies jsDelivr server-side (Origin Shield +
 * 1-year cache); see terraform/modules/app/main.tf (`jsdelivr-pyodide` origin +
 * `/pyodide-cdn/*` behavior).
 *
 * ON BY DEFAULT in production. Opt OUT per-browser (fall back to jsDelivr) from
 * the console:
 *     localStorage.setItem('disable-pyodide-self-host', '1'); location.reload()
 * Re-enable by removing it:
 *     localStorage.removeItem('disable-pyodide-self-host'); location.reload()
 *
 * No-op on local dev / prod preview — the /pyodide-cdn proxy only exists on our
 * deployed CloudFront, so there we keep the extension's jsDelivr default.
 *
 * ⚠️ DEPLOY ORDER: the CloudFront `/pyodide-cdn/*` behavior must be live BEFORE
 * a frontend deploy ships this, or every notebook request for pyodide.js falls
 * through to the SPA (index.html) and breaks.
 *
 * Version is pinned to the datalayer pyodide-kernel-extension default (v0.27.1);
 * the proxy is a transparent passthrough, so the same paths resolve. NOTE: only
 * the runtime + Pyodide-built packages (numpy, pandas, sqlite3, ssl, ipython,
 * micropip) go through here; the piplite kernel wheels (ipykernel, comm,
 * pyodide_kernel, piplite) still resolve via PyPI — a separate follow-up.
 */
const PLUGIN_ID = '@datalayer/jupyter-react/pyodide-kernel-extension:kernel'
const PYODIDE_VERSION = 'v0.27.1'
const DISABLE_FLAG = 'disable-pyodide-self-host'

export function configurePyodideSelfHosting() {
  if (typeof window === 'undefined') return

  const { hostname, origin } = window.location
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  if (isClientDevEnv || isLocalhost) return

  // On by default; opt out per-browser if the proxy ever misbehaves.
  try {
    if (window.localStorage.getItem(DISABLE_FLAG) === '1') return
  } catch {
    // localStorage unavailable (privacy mode) — proceed with the default.
  }

  const base = `${origin}/pyodide-cdn/pyodide/${PYODIDE_VERSION}/full`

  let settings: Record<string, unknown>
  try {
    settings = JSON.parse(PageConfig.getOption('litePluginSettings') || '{}')
  } catch {
    settings = {}
  }

  const existing = (settings[PLUGIN_ID] as Record<string, unknown>) ?? {}
  const existingLoadOptions =
    (existing.loadPyodideOptions as Record<string, unknown>) ?? {}

  settings[PLUGIN_ID] = {
    ...existing,
    pyodideUrl: `${base}/pyodide.js`,
    loadPyodideOptions: {
      ...existingLoadOptions,
      indexURL: `${base}/`,
    },
  }

  PageConfig.setOption('litePluginSettings', JSON.stringify(settings))
}
